import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowRight } from "lucide-react"

import { DataState } from "@/components/DataState"
import { ReasonDialog } from "@/components/ReasonDialog"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { formatAmount, formatPercent } from "@/lib/format"

import {
  confirmarPar,
  fetchContrapartes,
  fetchSugerencias,
  type Sugerencia,
} from "@/routes/holding/api"
import { t } from "@/lib/i18n"

/**
 * Operaciones entre sociedades del grupo.
 *
 * LO DIFICIL NO ES ELIMINARLAS: ES ENCONTRARLAS. Eliminar es una resta; saber
 * QUE restar es el trabajo. Por eso la pantalla tiene dos mitades y en este
 * orden:
 *
 *   1. Lo que Marty ya detecto solo, por identificador fiscal.
 *   2. Lo que SOSPECHA por parecido de nombre y necesita que alguien confirme.
 *
 * La segunda mitad es la que importa, porque el caso real del dato esta ahi: una
 * parte que se llama «Siete» y llego sin identificador fiscal. Y por eso
 * confirmar EXIGE MOTIVO: a partir de ese clic, todo lo que esa parte facture
 * desaparece del consolidado. Aceptar mal una sugerencia borra una venta real.
 */
export function IntercompaniaPage() {
  const qc = useQueryClient()
  const { toast } = useToast()
  const [porConfirmar, setPorConfirmar] = React.useState<Sugerencia | null>(null)

  const contrapartes = useQuery({
    queryKey: ["intercompania", "contrapartes"],
    queryFn: fetchContrapartes,
  })
  const sugerencias = useQuery({
    queryKey: ["intercompania", "sugerencias"],
    queryFn: fetchSugerencias,
  })

  const confirmar = useMutation({
    mutationFn: (v: { party_id: string; entity_id: string; motivo: string }) =>
      confirmarPar(v),
    onSuccess: () => {
      toast({ title: "Confirmado. Marty lo anotó." })
      setPorConfirmar(null)
      void qc.invalidateQueries({ queryKey: ["intercompania"] })
      void qc.invalidateQueries({ queryKey: ["consolidado"] })
    },
    onError: (e: unknown) => {
      toast({
        variant: "destructive",
        title: "No se pudo confirmar",
        description: e instanceof Error ? e.message : String(e),
      })
    },
  })

  const total = contrapartes.data?.total ?? 0

  return (
    <div className="space-y-3">
      {/* ------------------------------------------------ Ya detectadas */}
      <DataState
        loading={contrapartes.isLoading}
        error={contrapartes.error}
        onRetry={() => void contrapartes.refetch()}
        rows={4}
        columns={[30, 30, 20]}
      >
        <WorkPanel
          title={t("Contrapartes del grupo")}
          meta={`${total} detectada(s)`}
          footnote={
            contrapartes.data?.nota ??
            "Detectadas comparando identificadores fiscales normalizados: el CNPJ llega con puntos y barras, y el RUC panameño con espacios y un «DV»."
          }
        >
          {total === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">
              {t("Ninguna parte coincide con una sociedad del grupo por identificador fiscal.")}</p>
          ) : (
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[18rem]">{t("Parte (como llegó)")}</TableHead>
                  <TableHead className="w-[18rem]">{t("Es en realidad")}</TableHead>
                  <TableHead className="w-40">{t("Cómo se supo")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contrapartes.data?.contrapartes.map((c) => (
                  <TableRow key={c.party_id}>
                    <TableCell className="truncate" title={c.party_razon_social}>
                      {c.party_razon_social}
                    </TableCell>
                    <TableCell className="truncate font-medium" title={c.razon_social}>
                      {c.razon_social}
                    </TableCell>
                    <TableCell>
                      <Badge variant={c.origen === "confirmado" ? "brand-soft" : "success-soft"}>
                        {c.origen === "confirmado" ? "confirmada a mano" : "identificador fiscal"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </WorkPanel>
      </DataState>

      {/* ------------------------------------------------- Sugerencias */}
      <DataState
        loading={sugerencias.isLoading}
        error={sugerencias.error}
        empty={!sugerencias.isLoading && (sugerencias.data?.length ?? 0) === 0}
        emptyMessage={t("No hay nada que se parezca a una sociedad del grupo.")}
        emptyHint={t("Marty compara los nombres de tus proveedores y clientes contra los de tus sociedades. Si alguno se parece, aparece aquí para que lo confirmes.")}
        onRetry={() => void sugerencias.refetch()}
        rows={3}
        columns={[26, 16, 26, 12, 20]}
      >
        <WorkPanel
          title={t("Marty sospecha que estas son sociedades tuyas")}
          meta={`${sugerencias.data?.length ?? 0} por confirmar`}
          footnote={t("Ninguna se acepta sola, por alto que sea el parecido: confirmar hace que todo lo que esa parte facture desaparezca del consolidado, y equivocarse borra una venta real.")}
        >
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[16rem]">{t("Parte")}</TableHead>
                <TableHead className="w-32">{t("Identificador")}</TableHead>
                <TableHead className="w-[16rem]">{t("Se parece a")}</TableHead>
                <TableHead className="w-24 text-right">{t("Parecido")}</TableHead>
                <TableHead>{t("Por qué no se detectó solo")}</TableHead>
                <TableHead className="w-28 text-right">{t("Acciones")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sugerencias.data?.map((s) => (
                <TableRow key={s.party_id}>
                  <TableCell className="truncate font-medium" title={s.party_razon_social}>
                    {s.party_razon_social}
                  </TableCell>
                  <TableCell className="code truncate text-muted-foreground" title={s.party_tax_id}>
                    {s.party_tax_id}
                  </TableCell>
                  <TableCell
                    className="truncate"
                    title={
                      s.ambigua
                        ? (s.empata_con ?? []).map((e) => e.razon_social).join(" · ")
                        : s.entity_razon_social ?? ""
                    }
                  >
                    {/*
                      CON EMPATE NO SE PINTA UN CANDIDATO. «Siete» se parece
                      igual a las cuatro sociedades: mostrar una daría a entender
                      que Marty sabe cuál, y no lo sabe. Se listan las que
                      empatan y elige quien mira el comprobante.
                    */}
                    {s.ambigua ? (
                      <span className="text-2xs text-muted-foreground">
                        {t("Empata con")}{" "}{(s.empata_con ?? []).length}:{" "}
                        {(s.empata_con ?? []).map((e) => e.pais).join(" · ")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                        {s.entity_razon_social}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="num">{formatPercent(s.parecido)}</TableCell>
                  <TableCell className="clamp-2 text-2xs text-muted-foreground">
                    {s.por_que_no_se_propone ?? s.por_que_no_se_detecto}
                  </TableCell>
                  <TableCell className="text-right">
                    {/*
                      Sin candidato no hay nada que confirmar de un clic. Se
                      resuelve en la ficha de la parte, eligiendo la sociedad a
                      mano — que es lo que corresponde cuando el sistema no
                      puede saberlo.
                    */}
                    <Button
                      size="xs"
                      variant="outline"
                      disabled={s.ambigua || !s.entity_id}
                      title={s.ambigua ? (s.por_que_no_se_propone ?? undefined) : undefined}
                      onClick={() => setPorConfirmar(s)}
                    >
                      {t("Confirmar")}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </WorkPanel>
      </DataState>

      {porConfirmar ? (
        <ReasonDialog
          open
          onOpenChange={(v) => {
            if (!v) setPorConfirmar(null)
          }}
          title={t("Confirmar que es una sociedad del grupo")}
          description={
            `A partir de ahora, todo lo que «${porConfirmar.party_razon_social}» facture o reciba ` +
            `se elimina del consolidado, porque el grupo no se vende a sí mismo. ` +
            `Si te equivocas, desaparece una venta real.`
          }
          confirmLabel={t("Sí, es nuestra")}
          pending={confirmar.isPending}
          onConfirm={(motivo) =>
            confirmar.mutate({
              party_id: porConfirmar.party_id,
              entity_id: porConfirmar.entity_id as string,
              motivo,
            })
          }
        />
      ) : null}
    </div>
  )
}
